import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { useApi } from '@fortem-monorepo/react-spa'
import { Attributions } from '@fortem-monorepo/web-sdk'

import { SchemaType as AddAttributionEntrySchema } from '@/components/forms/AddAttributionEntryForm/AddAttributionEntryForm.schema'
import { SchemaType as RunDataExtractSchema } from '@/components/forms/RunDataExtractForm/RunDataExtractForm.schema'
import { toastApiError } from '@/lib/error-toast'

const useAddAttributionsMutation = () => {
  const api = useApi()

  return useMutation({
    mutationFn: (files: FileList) => {
      return api.attributions.dataPresence.uploadOpsAttributions({ files })
    },
    onSuccess: (_data, _params) => {
      toast.success('Upload successful')
    },
  })
}

const useAddScheduleMutation = () => {
  const api = useApi()

  return useMutation({
    mutationFn: (files: FileList) => {
      return api.attributions.dataPresence.uploadOpsSchedules({ files })
    },
    onSuccess: (_data, _params) => {
      toast.info('Starting Cash Flows process, check logs for more information.')
    },
  })
}

const useAddJPMValudationsMutation = () => {
  const api = useApi()

  return useMutation({
    mutationFn: (files: FileList) => {
      return api.attributions.dataPresence.uploadJPMValuations({ files })
    },
    onSuccess: (_data, _params) => {
      toast.success('Upload successful')
    },
  })
}

export const useAddAttributionEntryMutation = (
  input_type: AddAttributionEntrySchema['input_type'],
) => {
  const attributions = useAddAttributionsMutation()
  const schedule = useAddScheduleMutation()
  const jpm = useAddJPMValudationsMutation()

  const map = {
    attributions,
    schedule,
    jpm,
  } as const

  return map[input_type]
}

const useRunBanksExtractMutation = () => {
  const api = useApi()

  return useMutation({
    mutationFn: (body: Attributions.RunDataExtractBody) => {
      return api.attributions.dataPresence.runBanksExtracts(body)
    },
    onSuccess: (_data, _params) => {
      toast.success(`Succesfully ran extract for ${_params.value_date}`)
    },
  })
}

const useRunCustodiansExtractMutation = () => {
  const api = useApi()

  return useMutation({
    mutationFn: (body: Attributions.RunDataExtractBody) => {
      return api.attributions.dataPresence.runCustodiansExtracts(body)
    },
    onSuccess: (_data, _params) => {
      toast.success(`Succesfully ran extract for ${_params.value_date}`)
    },
  })
}

export const useRunDataExtractMutation = (input_type: RunDataExtractSchema['input_type']) => {
  const banks = useRunBanksExtractMutation()
  const custodians = useRunCustodiansExtractMutation()

  const map = {
    banks,
    custodians,
  } as const

  return map[input_type]
}

export const useRunRiskPremiaMutation = () => {
  const api = useApi()

  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (body: Attributions.GetDiscrepanciesParams) => {
      return api.attributions.riskPremia.runRiskPremia(body)
    },
    onSuccess: (_data, _params) => {
      toast.success(
        `Successfully ran Risk Premia for ${_params.fund_name} on ${_params.value_date}`,
      )
    },
    onError: (error, _params) => {
      toastApiError(
        error,
        `Error running Risk Premia for ${_params.fund_name} on ${_params.value_date}`,
      )
    },
    onSettled: (_data, _error, params) => {
      queryClient.invalidateQueries({
        queryKey: ['attributions', 'risk-premia', 'pnl', params],
      })
      queryClient.invalidateQueries({
        queryKey: ['attributions', 'risk-premia', 'securities', params],
      })
      queryClient.invalidateQueries({
        queryKey: ['attributions', 'risk-premia', 'final-attributions', params],
      })
      queryClient.invalidateQueries({
        queryKey: ['attributions', 'risk-premia', 'chart', params],
      })
    },
  })
}

export const useValidateFinalAttributionsMutation = () => {
  const api = useApi()

  return useMutation({
    mutationFn: (body: Attributions.GetDiscrepanciesParams) => {
      return api.attributions.riskPremia.validateFinalAttributions(body)
    },
    onSuccess: (_data, _params) => {
      toast.success(
        `Successfully validated Final Attributions for ${_params.fund_name} on ${_params.value_date}`,
      )
    },
    onError: (error, _params) => {
      toastApiError(
        error,
        `Error validating Final Attributions for ${_params.fund_name} on ${_params.value_date}`,
      )
    },
  })
}

export const useUpdatePnlDiscrepancyMutation = () => {
  const api = useApi()

  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (body: Attributions.UpdatePNLDiscrepancyBody) => {
      return api.attributions.riskPremia.updatePNLDiscrepancy(body)
    },
    onSuccess: (_data, _params) => {
      toast.success(`Successfully updated ${_params.oldValue} to ${_params.oldValue}`)
    },
    onError: (error, _params) => {
      toastApiError(error, `Error updating ${_params.updatedField} to ${_params.newValue}`)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['attributions', 'risk-premia', 'pnl'] })
    },
  })
}

export const useRunFixedIncomeMutation = () => {
  const api = useApi()

  return useMutation({
    mutationFn: (body: Attributions.RunFixedIncomeBody) => {
      return api.attributions.fixedIncome.run(body)
    },
    onSuccess: (_data, _params) => {
      toast.success(`Fixed income started`)
    },
  })
}

export const useSendFixedIncomeEmailMutation = () => {
  const api = useApi()

  return useMutation({
    mutationFn: (body: Attributions.SendFixedIncomeEmailBody) => {
      return api.attributions.fixedIncome.sendEmail(body)
    },
    onSuccess: (_data, _params) => {
      toast.success(`Email sent`)
    },
  })
}
